import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { inr, fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { ContextPanel } from "@/components/pawkt/ContextPanel";
import { Sparkles, PawPrint } from "lucide-react";
import { toast } from "sonner";

const COLS = [
  ["scheduled", "Scheduled"], ["checked_in", "Checked In"], ["waiting", "Waiting"],
  ["in_grooming", "In Grooming"], ["quality_check", "Quality Check"], ["ready", "Ready"], ["completed", "Completed"],
  ["unable_to_complete", "Unable to Complete"],
];
const NEXT = {
  scheduled: "checked_in", checked_in: "waiting", waiting: "in_grooming",
  in_grooming: "quality_check", quality_check: "ready", ready: "completed",
};

export default function Grooming() {
  const qc = useQueryClient();
  const { data: dash } = useQuery({ queryKey: ["grooming-dash"], queryFn: async () => (await api.get("/grooming/dashboard")).data });
  const { data: tickets, isLoading } = useQuery({ queryKey: ["grooming-tickets"], queryFn: async () => (await api.get("/grooming/tickets")).data });
  const [ctx, setCtx] = useState(null);

  const move = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/grooming/tickets/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["grooming-tickets"] }); qc.invalidateQueries({ queryKey: ["grooming-dash"] }); toast.success("Ticket updated"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const cap = dash?.capacity || {};
  const rev = dash?.revenue || {};
  const cards = [
    ["Today", dash?.today_appointments ?? 0], ["Groomers", cap.groomers ?? 0],
    ["Resources", cap.resources ?? 0], ["Collected Today", inr(rev.today || 0)], ["Grooming Due", dash?.retention?.grooming_due ?? 0],
  ];

  const grouped = (status) => (tickets || []).filter((t) => t.status === status);

  return (
    <div>
      <PageHeader title="Grooming" subtitle="Operational board · check-in → completed" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {cards.map(([l, v]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`grooming-kpi-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
            <p className="mt-1 text-2xl font-bold tabular text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      {isLoading ? <Loading /> : ((tickets?.length ?? 0) === 0 ? (
        <Empty icon={Sparkles} title="No grooming tickets" subtitle="Create a booking, then check the pet in to start a grooming ticket." />
      ) : (
        <div className="mt-5 grid gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="grooming-board">
          {COLS.map(([key, label]) => (
            <div key={key} className="rounded-xl border border-slate-200 bg-slate-50/60 p-2" data-testid={`grooming-col-${key}`}>
              <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{label} · {grouped(key).length}</p>
              <div className="space-y-2">
                {grouped(key).map((t) => (
                  <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid={`grooming-ticket-${t.id}`}>
                    <p className="text-sm font-semibold text-slate-900">{t.ticket_no}</p>
                    <p className="text-xs text-slate-500">{t.service_name}</p>
                    <p className="mt-1 text-xs text-slate-400">{fdt(t.start_time)}</p>
                    <button title="Context" data-testid={`ctx-open-${t.id}`} onClick={() => setCtx({ customerId: t.customer_id, petId: t.pet_id, vertical: "grooming" })} className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-600 hover:underline"><PawPrint className="h-3 w-3" /> Context</button>
                    {t.pricing?.total ? <p className="mt-1 text-xs font-medium text-emerald-700">{inr(t.pricing.total)}</p> : null}
                    {NEXT[t.status] && (
                      <button onClick={() => move.mutate({ id: t.id, status: NEXT[t.status] })}
                        data-testid={`grooming-advance-${t.id}`}
                        className="mt-2 w-full rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-700">
                        → {COLS.find(([k]) => k === NEXT[t.status])?.[1]}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
      {ctx && <ContextPanel open={!!ctx} onOpenChange={(o) => !o && setCtx(null)} customerId={ctx.customerId} petId={ctx.petId} vertical={ctx.vertical} />}
    </div>
  );
}
